import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

const defaultTheme = {
  accent: "#0f766e",
  accentSoft: "rgba(15,118,110,0.12)",
  accentBorder: "rgba(15,118,110,0.24)",
  surface: "rgba(255,255,255,0.9)",
  text: "#e5e7eb",
  textMuted: "#94a3b8",
  border: "rgba(148,163,184,0.18)",
  positive: "#34d399",
  negative: "#f87171",
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const fmtNum = (value, digits = 1) => {
  const n = toNumber(value);
  return n == null ? "—" : n.toFixed(digits);
};

const fmtPct = (value, digits = 1) => {
  const n = toNumber(value);
  if (n == null) return "—";
  return `${(n <= 1 ? n * 100 : n).toFixed(digits)}%`;
};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const normaliseText = (value) => String(value || "").trim();

const candidateArrays = (payload, side) => {
  const sideObj = payload?.[side] || payload?.teams?.[side] || payload?.[`${side}_team`] || null;
  return [
    payload?.[`${side}_last5`],
    payload?.[`${side}_form`],
    payload?.[`${side}_recent`],
    sideObj?.last5,
    sideObj?.form,
    sideObj?.recent,
    sideObj?.games,
  ].find(Array.isArray) || [];
};

const detectResult = (row = {}, teamName) => {
  const explicit = String(firstValue(row.result, row.outcome, row.form, row.wdl, row.status) || "").toUpperCase();
  if (explicit === "W" || explicit === "L" || explicit === "D") return explicit;

  const isWin = firstValue(row.is_win, row.win, row.won);
  if (typeof isWin === "boolean") return isWin ? "W" : "L";

  const teamScore = toNumber(firstValue(row.team_score, row.gf, row.scored, row.points_for, row.goals_for));
  const oppScore = toNumber(firstValue(row.opp_score, row.ga, row.allowed, row.points_against, row.goals_against));
  if (teamScore != null && oppScore != null) {
    if (teamScore > oppScore) return "W";
    if (teamScore < oppScore) return "L";
    return "D";
  }

  const homeScore = toNumber(firstValue(row.home_score, row.home_points, row.home_goals));
  const awayScore = toNumber(firstValue(row.away_score, row.away_points, row.away_goals));
  const homeTeam = normaliseText(firstValue(row.home_team, row.home, row.home_name));
  const awayTeam = normaliseText(firstValue(row.away_team, row.away, row.away_name));
  const normalisedTeam = normaliseText(teamName);
  if (homeScore != null && awayScore != null && normalisedTeam) {
    if (normalisedTeam === homeTeam) return homeScore > awayScore ? "W" : homeScore < awayScore ? "L" : "D";
    if (normalisedTeam === awayTeam) return awayScore > homeScore ? "W" : awayScore < homeScore ? "L" : "D";
  }

  return "";
};

const formatScoreLine = (row = {}) => {
  const parts = [
    firstValue(row.team_score, row.gf, row.scored, row.points_for, row.goals_for),
    firstValue(row.opp_score, row.ga, row.allowed, row.points_against, row.goals_against),
  ];
  if (parts[0] != null && parts[1] != null) return `${parts[0]}-${parts[1]}`;

  const homeScore = firstValue(row.home_score, row.home_points, row.home_goals);
  const awayScore = firstValue(row.away_score, row.away_points, row.away_goals);
  if (homeScore != null && awayScore != null) return `${homeScore}-${awayScore}`;

  return "";
};

const formatOpponent = (row = {}, teamName) => {
  const explicit = firstValue(row.opponent, row.opp, row.opp_name, row.opponent_name);
  if (explicit) return explicit;

  const homeTeam = normaliseText(firstValue(row.home_team, row.home, row.home_name));
  const awayTeam = normaliseText(firstValue(row.away_team, row.away, row.away_name));
  const normalisedTeam = normaliseText(teamName);
  if (!normalisedTeam) return firstValue(homeTeam, awayTeam, "Opponent");
  if (normalisedTeam === homeTeam) return awayTeam || "Opponent";
  if (normalisedTeam === awayTeam) return homeTeam || "Opponent";
  return firstValue(homeTeam, awayTeam, "Opponent");
};

const parseFormRows = (payload, side, teamName) =>
  candidateArrays(payload, side)
    .slice(0, 5)
    .map((row, index) => ({
      id: `${side}-${index}-${formatOpponent(row, teamName)}`,
      result: detectResult(row, teamName),
      opponent: formatOpponent(row, teamName),
      score: formatScoreLine(row),
    }));

const flattenPredictions = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "object") return [];
  return Object.entries(value).map(([key, entry]) => {
    if (entry && typeof entry === "object") return { key, ...entry };
    return { key, value: entry };
  });
};

const extractLeanFromEntry = (entry, fallbackLabel = "") => {
  if (!entry) return null;
  const label = normaliseText(
    firstValue(entry.market, entry.label, entry.name, entry.key, entry.type, fallbackLabel)
  );
  const lean = normaliseText(
    firstValue(entry.lean, entry.pick, entry.selection, entry.recommendation, entry.predicted_side, entry.side, entry.value)
  );
  const confidence = firstValue(entry.confidence, entry.confidence_pct, entry.win_prob, entry.prob, entry.probability);
  if (!label || !lean) return null;
  return {
    label,
    lean,
    confidence: confidence == null ? null : confidence,
  };
};

const pickPredictionGroups = (predictions) => {
  const entries = flattenPredictions(predictions)
    .map((entry) => extractLeanFromEntry(entry, entry?.key))
    .filter(Boolean);

  const findMatch = (patterns) =>
    entries.find((entry) => patterns.some((pattern) => entry.label.toLowerCase().includes(pattern)));

  return {
    moneyline: findMatch(["moneyline", "money line", "winner", "ml"]),
    spread: findMatch(["spread", "handicap", "line", "puck"]),
    totals: findMatch(["total", "totals", "over/under", "over under", "ou"]),
  };
};

const statCardsForSport = (sport, stats) => {
  if (!stats) return [];
  if (sport === "nba" || sport === "nfl") {
    return [
      { label: "PPG", value: fmtNum(firstValue(stats.pts_for_avg, stats.ppg, stats.points_per_game, stats.gf_avg), 1) },
      { label: "Allowed", value: fmtNum(firstValue(stats.pts_against_avg, stats.points_allowed_per_game, stats.pa_pg, stats.ga_avg), 1) },
      { label: "Win rate", value: fmtPct(stats.win_rate, 1) },
    ];
  }
  return [
    { label: "GF/G", value: fmtNum(firstValue(stats.goals_for_per_game, stats.gf_per_game, stats.gf_avg), 2) },
    { label: "GA/G", value: fmtNum(firstValue(stats.goals_against_per_game, stats.ga_per_game, stats.ga_avg), 2) },
    { label: "Win rate", value: fmtPct(stats.win_rate, 1) },
  ];
};

function TeamFormStrip({ title, rows, theme }) {
  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 16,
        padding: 14,
        background: theme.surface,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{title}</div>
      <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
        {rows.length ? (
          rows.map((row) => {
            const tone =
              row.result === "W" ? theme.positive : row.result === "L" ? theme.negative : theme.textMuted;
            return (
              <div
                key={row.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    minWidth: 28,
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: `${tone}18`,
                    color: tone,
                    fontSize: 12,
                    fontWeight: 800,
                    textAlign: "center",
                  }}
                >
                  {row.result || "—"}
                </span>
                <span style={{ fontSize: 13, color: theme.text }}>{row.opponent || "Opponent"}</span>
                <span style={{ fontSize: 12, color: theme.textMuted }}>{row.score || "—"}</span>
              </div>
            );
          })
        ) : (
          <div style={{ fontSize: 13, color: theme.textMuted }}>Recent form not available yet.</div>
        )}
      </div>
    </div>
  );
}

function SeasonStatCard({ title, stats, theme }) {
  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 16,
        padding: 14,
        background: theme.surface,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{title}</div>
      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
        {stats.map((stat) => (
          <div key={stat.label}>
            <div style={{ fontSize: 11, color: theme.textMuted, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {stat.label}
            </div>
            <div style={{ marginTop: 4, fontSize: 18, fontWeight: 700, color: theme.text }}>{stat.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NflResultStrip({ games, theme }) {
  const rows = Array.isArray(games) ? [...games].slice(-5).reverse() : [];
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
      {rows.length ? rows.map((game, index) => {
        const tone = game.result === "W" ? theme.positive : game.result === "L" ? theme.negative : theme.textMuted;
        return (
          <span key={`${game.week || index}-${game.opponent}`} title={`${game.venue === "home" ? "vs" : "at"} ${game.opponent} · ${game.points_for}-${game.points_against}`}
            style={{ minWidth: 35, padding: "6px 8px", borderRadius: 9, textAlign: "center", background: `${tone}18`, border: `1px solid ${tone}35`, color: tone, fontSize: 12, fontWeight: 900 }}>
            {game.result} {game.points_for}-{game.points_against}
          </span>
        );
      }) : <span style={{ color: theme.textMuted, fontSize: 12 }}>Recent results pending</span>}
    </div>
  );
}

function NflTeamSummary({ name, stats, venue, theme }) {
  if (!stats) return <div style={{ padding: 18, color: theme.textMuted }}>Team baseline pending</div>;
  const diff = toNumber(stats.pts_diff_per_game);
  return (
    <div style={{ padding: 18, borderRadius: 18, border: `1px solid ${theme.border}`, background: `linear-gradient(145deg, ${theme.surface}, ${theme.accentSoft})` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div><div style={{ color: theme.textMuted, fontSize: 10, fontWeight: 900, letterSpacing: 1, textTransform: "uppercase" }}>{venue}</div><div style={{ marginTop: 5, color: theme.text, fontSize: 17, fontWeight: 900 }}>{name}</div></div>
        <div style={{ textAlign: "right" }}><div style={{ color: theme.accent, fontSize: 25, lineHeight: 1, fontWeight: 900 }}>{stats.record || "—"}</div><div style={{ marginTop: 5, color: theme.textMuted, fontSize: 11 }}>season record</div></div>
      </div>
      <NflResultStrip games={stats.recent_games} theme={theme} />
      <div style={{ marginTop: 15, display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8 }}>
        {[
          ["Point diff", diff == null ? "—" : `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}`, diff == null ? theme.textMuted : diff >= 0 ? theme.positive : theme.negative],
          ["Last 5", stats.recent_record || "—", theme.text],
          [venue === "Home" ? "At home" : "On road", venue === "Home" ? stats.home_record : stats.away_record, theme.text],
        ].map(([label, value, color]) => <div key={label} style={{ padding: "10px 8px", borderRadius: 11, background: "rgba(15,23,42,0.055)" }}><div style={{ color: theme.textMuted, fontSize: 9, fontWeight: 800, textTransform: "uppercase" }}>{label}</div><div style={{ marginTop: 4, color, fontSize: 16, fontWeight: 900 }}>{value || "—"}</div></div>)}
      </div>
    </div>
  );
}

function NflComparisonRow({ label, awayValue, homeValue, lowerIsBetter = false, theme }) {
  const away = toNumber(awayValue);
  const home = toNumber(homeValue);
  const max = Math.max(away || 0, home || 0, 1);
  const awayWins = away != null && home != null && (lowerIsBetter ? away < home : away > home);
  const homeWins = away != null && home != null && (lowerIsBetter ? home < away : home > away);
  return (
    <div style={{ padding: "12px 0", borderTop: `1px solid ${theme.border}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 92px 1fr 60px", alignItems: "center", gap: 8 }}>
        <b style={{ color: awayWins ? theme.positive : theme.text, textAlign: "right" }}>{fmtNum(away, 1)}</b>
        <div style={{ height: 7, borderRadius: 99, background: "rgba(148,163,184,.18)", overflow: "hidden", transform: "scaleX(-1)" }}><div style={{ height: "100%", width: `${((away || 0) / max) * 100}%`, background: awayWins ? theme.positive : theme.accent, borderRadius: 99 }} /></div>
        <div style={{ color: theme.textMuted, fontSize: 10, fontWeight: 900, textAlign: "center", textTransform: "uppercase" }}>{label}</div>
        <div style={{ height: 7, borderRadius: 99, background: "rgba(148,163,184,.18)", overflow: "hidden" }}><div style={{ height: "100%", width: `${((home || 0) / max) * 100}%`, background: homeWins ? theme.positive : theme.accent, borderRadius: 99 }} /></div>
        <b style={{ color: homeWins ? theme.positive : theme.text }}>{fmtNum(home, 1)}</b>
      </div>
    </div>
  );
}

function NflMatchupBoard({ fixture, homeStats, awayStats, theme }) {
  const hasHomeStats = !!(homeStats && Number(homeStats.games_played) > 0);
  const hasAwayStats = !!(awayStats && Number(awayStats.games_played) > 0);
  const season = firstValue(homeStats?.season, awayStats?.season);
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 10, color: theme.textMuted, fontSize: 11 }}><span>{fixture?.away_team || "Away"}</span><span>{season ? `${season} regular season · nflverse` : "NFL baseline"}</span><span>{fixture?.home_team || "Home"}</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12 }}>
        <NflTeamSummary name={fixture?.away_team || "Away"} stats={hasAwayStats ? awayStats : null} venue="Away" theme={theme} />
        <NflTeamSummary name={fixture?.home_team || "Home"} stats={hasHomeStats ? homeStats : null} venue="Home" theme={theme} />
      </div>
      {hasHomeStats && hasAwayStats ? <div style={{ marginTop: 12, padding: "5px 16px", borderRadius: 16, border: `1px solid ${theme.border}`, background: theme.surface }}>
        <NflComparisonRow label="Offense" awayValue={awayStats.pts_for_avg} homeValue={homeStats.pts_for_avg} theme={theme} />
        <NflComparisonRow label="Defense" awayValue={awayStats.pts_against_avg} homeValue={homeStats.pts_against_avg} lowerIsBetter theme={theme} />
        <NflComparisonRow label="Last 5 PPG" awayValue={awayStats.recent_pts_for_avg} homeValue={homeStats.recent_pts_for_avg} theme={theme} />
        <NflComparisonRow label="Game total" awayValue={awayStats.avg_game_total} homeValue={homeStats.avg_game_total} theme={theme} />
      </div> : null}
    </div>
  );
}

function LeanCard({ label, lean, confidence, theme }) {
  return (
    <div
      style={{
        border: `1px solid ${theme.accentBorder}`,
        borderRadius: 16,
        padding: 14,
        background: `linear-gradient(180deg, ${theme.accentSoft}, rgba(255,255,255,0.02))`,
      }}
    >
      <div style={{ fontSize: 11, color: theme.textMuted, textTransform: "uppercase", letterSpacing: 0.7 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: theme.text }}>{lean}</div>
      <div style={{ marginTop: 8, fontSize: 12, color: theme.textMuted }}>
        {confidence == null ? "Model lean ready" : `Confidence ${fmtPct(confidence, 1)}`}
      </div>
    </div>
  );
}

export default function MatchupModelSection({
  fixtureId,
  sport,
  fixture,
  homeStats,
  awayStats,
  predictions,
  modelProbs,
  theme,
}) {
  const mergedTheme = { ...defaultTheme, ...theme };
  const [formData, setFormData] = useState(null);
  const [formLoading, setFormLoading] = useState(true);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setFormLoading(true);
    setFormError("");
    setFormData(null);

    api
      .get("/form/fixture/json", {
        params: { fixture_id: fixtureId, n: 5 },
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      })
      .then(({ data }) => {
        if (!cancelled) setFormData(data || null);
      })
      .catch((error) => {
        if (!cancelled) setFormError(error?.message || "Could not load recent form.");
      })
      .finally(() => {
        if (!cancelled) setFormLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fixtureId]);

  const homeForm = useMemo(
    () => parseFormRows(formData, "home", fixture?.home_team),
    [formData, fixture?.home_team]
  );
  const awayForm = useMemo(
    () => parseFormRows(formData, "away", fixture?.away_team),
    [formData, fixture?.away_team]
  );

  const leans = useMemo(() => pickPredictionGroups(predictions), [predictions]);
  const leanCards = [
    leans.moneyline ? { title: "Moneyline", ...leans.moneyline } : null,
    leans.spread ? { title: sport === "nhl" ? "Spread / Puck Line" : "Spread", ...leans.spread } : null,
    leans.totals ? { title: "Totals", ...leans.totals } : null,
  ].filter(Boolean);

  const hasStats = !!(homeStats || awayStats);
  const fallbackMessage =
    !leanCards.length && (predictions == null || (typeof predictions === "object" && !Object.keys(predictions).length))
      ? "Model leans have not been published yet. Recent form and season averages are still available below."
      : "";

  return (
    <section
      style={{
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${mergedTheme.border}`,
        borderRadius: 20,
        padding: 18,
        marginBottom: 20,
        boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              borderRadius: 999,
              padding: "4px 10px",
              background: mergedTheme.accentSoft,
              color: mergedTheme.accent,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: "uppercase",
            }}
          >
            Matchup Model
          </div>
          <h3 style={{ margin: "10px 0 0", fontSize: 22, color: mergedTheme.text }}>
            {sport === "nba" ? "NBA" : sport === "nfl" ? "NFL" : "NHL"} matchup view
          </h3>
        </div>
        {modelProbs ? (
          <div style={{ fontSize: 12, color: mergedTheme.textMuted }}>
            Model probabilities loaded
          </div>
        ) : null}
      </div>

      {sport === "nfl" ? <NflMatchupBoard fixture={fixture} homeStats={homeStats} awayStats={awayStats} theme={mergedTheme} /> : <div style={{ marginTop: 16, display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <TeamFormStrip title={`${fixture?.home_team || "Home"} last 5`} rows={homeForm} theme={mergedTheme} />
        <TeamFormStrip title={`${fixture?.away_team || "Away"} last 5`} rows={awayForm} theme={mergedTheme} />
      </div>}

      {formLoading ? (
        <div style={{ marginTop: 10, fontSize: 12, color: mergedTheme.textMuted }}>Loading recent form…</div>
      ) : null}
      {!formLoading && formError ? (
        <div style={{ marginTop: 10, fontSize: 12, color: mergedTheme.textMuted }}>{formError}</div>
      ) : null}

      {hasStats && sport !== "nfl" ? (
        <div style={{ marginTop: 14, display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {homeStats ? (
            <SeasonStatCard title={`${fixture?.home_team || "Home"} season`} stats={statCardsForSport(sport, homeStats)} theme={mergedTheme} />
          ) : null}
          {awayStats ? (
            <SeasonStatCard title={`${fixture?.away_team || "Away"} season`} stats={statCardsForSport(sport, awayStats)} theme={mergedTheme} />
          ) : null}
        </div>
      ) : null}

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: mergedTheme.textMuted, textTransform: "uppercase", letterSpacing: 0.7 }}>
          Model leans
        </div>
        {leanCards.length ? (
          <div style={{ marginTop: 10, display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            {leanCards.map((lean) => (
              <LeanCard
                key={lean.title}
                label={lean.title}
                lean={lean.lean}
                confidence={lean.confidence}
                theme={mergedTheme}
              />
            ))}
          </div>
        ) : (
          <div
            style={{
              marginTop: 10,
              border: `1px solid ${mergedTheme.border}`,
              borderRadius: 14,
              padding: 14,
              fontSize: 13,
              color: mergedTheme.textMuted,
              background: "rgba(15,23,42,0.08)",
            }}
          >
            {fallbackMessage || "Model leans are not available for this fixture yet."}
          </div>
        )}
      </div>
    </section>
  );
}
